import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { DAYS_OF_WEEK, DayOfWeek, WeeklyAvailability } from "../../services/availability/types";
import { groupWeeklyByDay } from "../../utils/availability";

type WeeklyScheduleProps = {
  weekly: WeeklyAvailability[];
  /** Absent en lecture seule (secrétaire, confrère). */
  onAdd?: (day: DayOfWeek) => void;
  onEdit?: (range: WeeklyAvailability) => void;
};

const formatRange = (range: WeeklyAvailability) => `${range.startTime.slice(0, 5)} – ${range.endTime.slice(0, 5)}`;

const RANGE_PILL_CLASS =
  "inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700 tabular-nums";

/** Semaine type : une ligne par jour, les plages en pastilles. */
const WeeklySchedule: React.FC<WeeklyScheduleProps> = ({ weekly, onAdd, onEdit }) => {
  const rangesByDay = useMemo(() => groupWeeklyByDay(weekly), [weekly]);

  return (
    <ul className="divide-y divide-slate-100">
      {DAYS_OF_WEEK.map((day) => {
        const ranges = rangesByDay[day.value];
        return (
          <li key={day.value} className="group/row flex flex-wrap items-center gap-3 px-5 py-3">
            <span className="w-24 shrink-0 text-sm font-semibold text-slate-800">{day.label}</span>
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {ranges.length === 0 && <span className="text-sm text-slate-400">Pas de consultation</span>}
              {ranges.map((range) =>
                onEdit ? (
                  <button
                    key={range.id}
                    type="button"
                    onClick={() => onEdit(range)}
                    title="Modifier la plage"
                    className={cn(RANGE_PILL_CLASS, "transition-colors duration-200 hover:border-emerald-300 hover:bg-emerald-100")}
                  >
                    <i className="bx bx-time text-base" aria-hidden />
                    {formatRange(range)}
                  </button>
                ) : (
                  <span key={range.id} className={RANGE_PILL_CLASS}>
                    <i className="bx bx-time text-base" aria-hidden />
                    {formatRange(range)}
                  </span>
                )
              )}
            </div>
            {onAdd && (
              <button
                type="button"
                onClick={() => onAdd(day.value)}
                title={`Ajouter une plage le ${day.label.toLowerCase()}`}
                aria-label={`Ajouter une plage le ${day.label.toLowerCase()}`}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-emerald-700 opacity-70 transition-all duration-200 hover:bg-emerald-50 group-hover/row:opacity-100"
              >
                <i className="bx bx-plus text-lg" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default WeeklySchedule;
